import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography,
  TextField,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../theme";
import { GET, ADD } from "../Functions/apiFunction";
import api from "../Data/api";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocalDrinkOutlinedIcon from "@mui/icons-material/LocalDrinkOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";

function BottleReturn() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const admin = JSON.parse(sessionStorage.getItem("admin"));
  const token = `Bearer ${admin.token}`;

  const [tab, setTab] = useState(0); // 0=pending, 1=all
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [reFetch, setReFetch] = useState(false);
  const [search, setSearch] = useState("");

  const [snackOpen, setSnackOpen] = useState(false);
  const [alertType, setAlertType] = useState("info");
  const [alertMsg, setAlertMsg] = useState("");

  const showAlert = (type, msg) => {
    setAlertType(type);
    setAlertMsg(msg);
    setSnackOpen(true);
  };

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const statusParam = tab === 0 ? "?status=0" : "";
      const res = await GET(token, `${api}/bottle_return/all${statusParam}`);
      if (res?.response === 200) {
        setData(res.data || []);
      }
      setLoading(false);
    };
    fetch();
  }, [tab, reFetch, token]);

  const handleMarkCollected = async (id) => {
    setActionLoading(true);
    const res = await ADD(
      token,
      `${api}/bottle_return/mark_collected`,
      JSON.stringify({ id })
    );
    setActionLoading(false);
    if (res?.response === 200) {
      showAlert("success", "Bottle marked as collected");
      setReFetch((p) => !p);
    } else {
      showAlert("error", "Failed to update");
    }
  };

  const handleDelete = async (id) => {
    setActionLoading(true);
    const res = await ADD(
      token,
      `${api}/bottle_return/delete`,
      JSON.stringify({ id })
    );
    setActionLoading(false);
    if (res?.response === 200) {
      showAlert("success", "Deleted");
      setReFetch((p) => !p);
    } else {
      showAlert("error", "Failed to delete");
    }
  };

  const filtered = data.filter((row) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (row.customer_name || "").toLowerCase().includes(s) ||
      (row.product_title || "").toLowerCase().includes(s) ||
      (row.delivery_user_name || "").toLowerCase().includes(s) ||
      String(row.id).includes(s)
    );
  });

  const columns = [
    { field: "id", headerName: "ID", width: 60 },
    { field: "product_title", headerName: "Product", width: 160 },
    { field: "customer_name", headerName: "Customer", width: 140 },
    { field: "customer_phone", headerName: "Phone", width: 120 },
    { field: "area", headerName: "Area", width: 110 },
    { field: "city", headerName: "City", width: 100 },
    { field: "qty", headerName: "Qty", width: 60 },
    { field: "pending_date", headerName: "Pending Date", width: 120 },
    { field: "collected_date", headerName: "Collected Date", width: 130 },
    { field: "delivery_user_name", headerName: "Delivery Boy", width: 130 },
    { field: "note", headerName: "Note", width: 160 },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value === 0 || params.value === "0" ? "Pending" : "Collected"}
          size="small"
          color={params.value === 0 || params.value === "0" ? "warning" : "success"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          {(params.row.status === 0 || params.row.status === "0") && (
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={actionLoading}
              onClick={() => handleMarkCollected(params.row.id)}
              startIcon={<CheckCircleOutlineIcon />}
              sx={{ fontSize: "11px", textTransform: "none" }}
            >
              Collected
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={actionLoading}
            onClick={() => handleDelete(params.row.id)}
            sx={{ fontSize: "11px", textTransform: "none" }}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  const pendingCount = data.filter(
    (r) => r.status === 0 || r.status === "0"
  ).length;

  return (
    <Box sx={{ p: 2 }}>
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={alertType} onClose={() => setSnackOpen(false)} sx={{ width: "100%" }}>
          {alertMsg}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <LocalDrinkOutlinedIcon color="warning" sx={{ fontSize: 32 }} />
        <Typography variant="h3" component="h3">
          Bottle Return Tracking
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => setReFetch((p) => !p)}
        >
          Refresh
        </Button>
      </Stack>

      {/* Summary cards */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: colors.primary[400],
            textAlign: "center",
            minWidth: 160,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Pending Returns
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontWeight: "bold", color: pendingCount > 0 ? "#f59e0b" : "#10b981" }}
          >
            {pendingCount}
          </Typography>
        </Paper>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: colors.primary[400],
            textAlign: "center",
            minWidth: 160,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Total Records
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: "bold" }}>
            {data.length}
          </Typography>
        </Paper>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        textColor="secondary"
        indicatorColor="secondary"
        sx={{ mb: 2 }}
      >
        <Tab label={`Pending (${pendingCount})`} />
        <Tab label="All Records" />
      </Tabs>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search by customer, product, delivery boy..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: { xs: "100%", sm: 400 } }}
        color="secondary"
      />

      {/* Table */}
      {loading ? (
        <Stack alignItems="center" sx={{ mt: 6 }}>
          <CircularProgress color="secondary" />
        </Stack>
      ) : (
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            backgroundColor: colors.primary[400],
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.navbarBG[400],
              borderBottom: "none",
              color: "#f5f5f5",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.navbarBG[400],
              color: "#f5f5f5 !important",
            },
            "& .MuiTablePagination-root": { color: "#f5f5f5 !important" },
          }}
        >
          <DataGrid
            rows={filtered}
            columns={columns}
            pageSize={20}
            rowsPerPageOptions={[10, 20, 50]}
            autoHeight
            disableSelectionOnClick
            components={{
              Toolbar: () => (
                <GridToolbarContainer sx={{ p: 1 }}>
                  <GridToolbarExport color="secondary" />
                </GridToolbarContainer>
              ),
            }}
          />
        </Paper>
      )}
    </Box>
  );
}

export default BottleReturn;
